import { PlusOutlined } from '@ant-design/icons';
import AppButton from '@nxsys/components/AppButton';
import AppCard from '@nxsys/components/AppCard';
import AppInputSearch from '@nxsys/components/AppInputSearch';
import AppPageTitle from '@nxsys/components/AppPageTitle';
import AppsContainer from '@nxsys/components/AppsContainer';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppsHeader from '@nxsys/components/AppsContainer/AppsHeader';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import { useNavigateWithQuery } from '@nxsys/hooks/useNavigateWithQuery';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { AssignmentState } from '@nxsys/store/slices/assignment/reducer/assignmentSlice';
import { GetAssignmentListParams } from '@nxsys/store/slices/assignment/types';
import { debounce } from 'lodash';
import { FC, useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { getColumns } from './columns';

interface IBaseAssignmentListingProps {
  assignmentList: AssignmentState['assignmentList'];
  handleFetchData: (params: GetAssignmentListParams) => void;
}

const BaseAssignmentListing: FC<IBaseAssignmentListingProps> = ({
  assignmentList,
  handleFetchData,
}) => {
  const { messages } = useTypedIntl();
  const navigate = useNavigateWithQuery();
  const { isAgency, isEmployee } = useSelector(
    (state: AppState) => state.appStore,
  );

  const { loading, list, pagination, queryParams } = assignmentList;

  const [searchValue, setSearchValue] = useState('');
  const isFiltering = !!queryParams?.search;

  useEffect(() => {
    handleFetchData({
      page: 0,
      size: 20,
    });
  }, []);

  const debouncedFetchData = useCallback(
    debounce((search: string) => {
      handleFetchData({
        ...queryParams,
        page: 0,
        size: 20,
        search: search,
      });
    }, 500),
    [queryParams],
  );

  const renderTitle = useCallback(() => {
    return (
      <AppPageTitle
        prefix={messages['assignment.title']}
        separator=""
        title={''}
      />
    );
  }, [messages]);

  const renderRightContent = () => {
    if (isAgency || isEmployee) {
      return null;
    }

    return (
      <div className="flex items-center gap-2">
        <AppButton
          type="primary"
          name={messages['common.addNew']}
          onClick={() => {
            navigate('/assignments/create');
          }}
          leftIcon={<PlusOutlined />}
        />
      </div>
    );
  };

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    handleFetchData({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);
    debouncedFetchData(value);
  };

  const handleViewDetail = (id: string) => {
    navigate(`/assignments/${id}`, {
      query: { from: location.pathname },
    });
  };

  const onChangeTable = (sorter?: { field: string; order: 'desc' | 'asc' }) => {
    if (sorter?.order) {
      handleFetchData({
        ...queryParams,
        page: 0,
        sortField: sorter.field || '',
        sortType: sorter.order,
      });
    } else {
      handleFetchData({
        ...queryParams,
        page: 0,
        sortField: undefined,
        sortType: undefined,
      });
    }
  };

  const getNoDataType = () => {
    if (isEmployee || isAgency) {
      return 'noDataToShow';
    }

    if (isFiltering) {
      return 'noDataFound';
    }
    return 'noDataCreated';
  };

  return (
    <AppsContainer
      fullView
      type="bottom"
      title={renderTitle()}
      rightContent={renderRightContent()}
    >
      <AppCard>
        <AppsHeader>
          <AppInputSearch value={searchValue} onChange={handleSearchChange} />
        </AppsHeader>
        <AppsContent>
          <AppTableContainer
            data={list ?? []}
            loading={loading}
            page={pagination.current}
            total={pagination.totalDocs}
            pageSize={pagination.pageSize}
            scroll={{ x: 1500, y: 700 }}
            titleEmpty={messages['assignment.empty']}
            columns={getColumns({
              messages,
              handleViewDetail,
            })}
            onChangePagination={onChangePagination}
            onChangeTable={onChangeTable}
            noDataType={getNoDataType()}
            addNewBtnOnClick={() => {
              navigate('/assignments/create');
            }}
            dataTypeName={messages['common.assignment']}
          />
        </AppsContent>
      </AppCard>
    </AppsContainer>
  );
};

export default BaseAssignmentListing;
